// The browser's install prompt (Chrome, Edge, Samsung Internet: `beforeinstallprompt`). It fires once the browser
// considers the site installable, often before React mounts, so main.jsx calls captureInstallPrompt() first thing.
// preventDefault keeps Chrome's own mini-bar away: the site asks itself (utils/pwa). iOS has no prompt: there the site
// shows the steps (components/app/InstallSheet).

let state = { prompt: null, installed: false };
const listeners = new Set();
const installedListeners = new Set();
let detach = null;

const set = (next) => {
  state = { ...state, ...next };
  listeners.forEach((fn) => fn());
};

export const installState = () => state;

export const subscribeInstall = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Called once the app was installed from this tab (analytics). */
export const onAppInstalled = (fn) => {
  installedListeners.add(fn);
  return () => installedListeners.delete(fn);
};

export const captureInstallPrompt = (win = window) => {
  if (detach) return;
  const onPrompt = (event) => {
    event.preventDefault();
    set({ prompt: event });
  };
  const onInstalled = () => {
    set({ prompt: null, installed: true });
    installedListeners.forEach((fn) => fn());
  };
  win.addEventListener('beforeinstallprompt', onPrompt);
  win.addEventListener('appinstalled', onInstalled);
  detach = () => {
    win.removeEventListener('beforeinstallprompt', onPrompt);
    win.removeEventListener('appinstalled', onInstalled);
  };
};

/** Shows the browser's install prompt (inside the tap). The event is spent either way. */
export const promptInstall = async () => {
  const event = state.prompt;
  if (!event) return 'unavailable';
  set({ prompt: null });
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
  } catch {
    return 'unavailable';
  }
};

/** Tests: back to a fresh page. */
export const resetInstallPrompt = () => {
  detach?.();
  detach = null;
  state = { prompt: null, installed: false };
  listeners.clear();
  installedListeners.clear();
};
